import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  deleteModeloEtiqueta,
  duplicateModeloEtiqueta,
  fetchModelosEtiqueta,
  labelTemplateKeys,
} from '@/services/labelTemplateService';
import type { IModeloEtiqueta } from '@/features/label-editor/domain/types';
import './modelos-etiqueta.css';

export const Route = createFileRoute('/_authenticated/configuracao/modelos-etiqueta/')({
  component: ModelosEtiquetaListPage,
});

function ModelosEtiquetaListPage() {
  const queryClient = useQueryClient();
  const [modeloParaExcluir, setModeloParaExcluir] = useState<IModeloEtiqueta | null>(null);
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: labelTemplateKeys.list(),
    queryFn: () => fetchModelosEtiqueta(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: labelTemplateKeys.all });

  const deleteMutation = useMutation({
    mutationFn: deleteModeloEtiqueta,
    onSuccess: () => {
      setModeloParaExcluir(null);
      void invalidate();
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateModeloEtiqueta,
    onSuccess: invalidate,
  });

  const mutationPending = deleteMutation.isPending || duplicateMutation.isPending;

  return (
    <div className="modelos-etiqueta-page">
      <div className="modelos-etiqueta-page__header">
        <div>
          <p>Crie e gerencie os modelos visuais de etiquetas para impressão.</p>
        </div>
        <Link
          to="/configuracao/modelos-etiqueta/form"
          className="modelos-etiqueta-page__btn-primary"
        >
          Novo modelo
        </Link>
      </div>

      {isError && (
        <p className="modelos-etiqueta-page__error">
          {isAxiosError(error)
            ? (error.response?.data as { error?: { message?: string } })?.error?.message
              ?? error.message
            : 'Falha ao carregar modelos'}
        </p>
      )}
      {deleteMutation.isError && (
        <p className="modelos-etiqueta-page__error">Falha ao excluir modelo.</p>
      )}
      {duplicateMutation.isError && (
        <p className="modelos-etiqueta-page__error">Falha ao duplicar modelo.</p>
      )}

      <div className="modelos-etiqueta-page__table-container">
        <Table>
          <caption className="sr-only">Lista de modelos de etiqueta</caption>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Nome</Table.Head>
              <Table.Head scope="col">Tamanho</Table.Head>
              <Table.Head scope="col">DPI</Table.Head>
              <Table.Head scope="col">Status</Table.Head>
              <Table.Head scope="col">Atualizado</Table.Head>
              <Table.Head scope="col">Ações</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {isLoading && (
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="modelos-etiqueta-page__empty"
                >
                  Carregando…
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading &&
              data.map((modelo) => (
                <Table.Row key={modelo.id}>
                  <Table.Cell>{modelo.nome}</Table.Cell>
                  <Table.Cell>
                    {modelo.largura_mm} × {modelo.altura_mm} mm
                  </Table.Cell>
                  <Table.Cell>{modelo.dpi}</Table.Cell>
                  <Table.Cell>
                    <span
                      className={
                        modelo.ativo
                          ? 'modelos-etiqueta-page__status modelos-etiqueta-page__status--ativo'
                          : 'modelos-etiqueta-page__status modelos-etiqueta-page__status--inativo'
                      }
                    >
                      {modelo.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>{new Date(modelo.updated_at).toLocaleString('pt-BR')}</Table.Cell>
                  <Table.Cell className="modelos-etiqueta-page__actions">
                    <Link
                      to="/configuracao/modelos-etiqueta/form/$id"
                      params={{ id: modelo.id }}
                    >
                      Editar
                    </Link>
                    <button
                      type="button"
                      disabled={mutationPending}
                      onClick={() => duplicateMutation.mutate(modelo.id)}
                    >
                      Duplicar
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      disabled={mutationPending}
                      onClick={() => setModeloParaExcluir(modelo)}
                    >
                      Excluir
                    </button>
                  </Table.Cell>
                </Table.Row>
              ))}
            {!isLoading && !isError && data.length === 0 && (
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="modelos-etiqueta-page__empty"
                >
                  Nenhum modelo cadastrado.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Content>
        </Table>
      </div>

      {modeloParaExcluir && (
        <Modal
          onClose={() => setModeloParaExcluir(null)}
          className="modelos-etiqueta-page__modal"
        >
          <Modal.Header title="Excluir modelo" />
          <Modal.Content>
            <p>
              Tem certeza que deseja excluir o modelo{' '}
              <strong>&quot;{modeloParaExcluir.nome}&quot;</strong>? Essa ação não pode ser
              desfeita.
            </p>
          </Modal.Content>
          <Modal.Footer className="modelos-etiqueta-page__modal-footer">
            <button
              type="button"
              className="modelos-etiqueta-page__btn-secondary"
              disabled={deleteMutation.isPending}
              onClick={() => setModeloParaExcluir(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="modelos-etiqueta-page__btn-danger"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(modeloParaExcluir.id)}
            >
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
